import { useMutation } from "@apollo/client";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { CHANGE_PASSWORD } from "../../graphql/gqlUser";
import { SnackbarContext } from "../../contexts/snackbarContext";
import { T } from "../../components/T";

const PasswordSettings = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [changePassword] = useMutation(CHANGE_PASSWORD);
  const { openErrorSnackbar, openSuccessSnackbar } =
    useContext(SnackbarContext);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (password === "") {
      openErrorSnackbar(<T str="Current password is required" />);
      return;
    }

    if (newPassword === "") {
      openErrorSnackbar(<T str="New password is required" />);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      openErrorSnackbar(<T str="Passwords do not match" />);
      return;
    }

    setLoading(true);

    changePassword({ variables: { oldPassword: password, newPassword } })
      .then(() => {
        openSuccessSnackbar(<T str="Password updated" />);
        setLoading(false);
        setPassword("");
        setNewPassword("");
        setConfirmNewPassword("");
      })
      .catch((error) => {
        openErrorSnackbar(error.message || <T str="Something went wrong" />);
        setLoading(false);
      });
  };

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="currentPassword"
          label={<T str="Current password" />}
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          color="secondary"
          autoComplete="password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="newPassword"
          label={<T str="New password" />}
          type={showNewPassword ? "text" : "password"}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          color="secondary"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          variant="outlined"
          margin="normal"
          required
          fullWidth
          name="confirmNewPassword"
          label={<T str="Confirm new password" />}
          type={showConfirmNewPassword ? "text" : "password"}
          value={confirmNewPassword}
          onChange={(e) => setConfirmNewPassword(e.target.value)}
          color="secondary"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() =>
                    setShowConfirmNewPassword(!showConfirmNewPassword)
                  }
                >
                  {showConfirmNewPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <T str="Loading..." /> : <T str="Change password" />}
        </Button>
      </Box>
    </>
  );
};

export default PasswordSettings;
